import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useLocation } from 'react-router-dom';
import { FLAGS } from '../lib/flags';
import Sidebar from '../components/layout/Sidebar';
import ChipInput from '../components/swirlspace/ChipInput';
import MasonryGrid from '../components/swirlspace/MasonryGrid';
import deepsearchService from '../services/deepsearchService';
import contentService from '../services/contentService';
import { useDebounce } from '../hooks/useDebounce';
import { supabase } from '../lib/supabase';
import { ChevronRight, Palette, Grid3x3, Folder, Clock } from 'lucide-react';
import FloatingSidebar from '../components/swirlspace/FloatingSidebar';
import FilterLayer from '../components/swirlspace/FilterLayer';
import OrganizationLayer from '../components/swirlspace/OrganizationLayer';
// MoodSync removed - was causing green background issues
// import { setMoodFromSwirl, restoreMood, disableMood, cleanupMoodScrollDepth } from '../lib/moodsync';
import ContentDetailModal from '../components/modals/ContentDetailModal';
import DeleteConfirmationModal from '../components/modals/DeleteConfirmationModal';
import '../utils/embeddingMonitor'; // Load monitoring utilities
import personalizationService from '../services/personalizationService';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useBehavioralLearning } from '../hooks/useBehavioralLearning';
import smartSwirlsService from '../services/smartSwirlsService';
import { itemMatchesTheme, resetDebugCount } from '../lib/themeClassifier';

export default function SwirlspacePage() {
  const { user } = useAuth();
  const { isExpanded, toggle } = useSidebar();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Phase 9.5.2b: Enable behavioral learning
  useBehavioralLearning(user?.id);
  const [chips, setChips] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItemCount, setTotalItemCount] = useState(null);
  const PAGE_SIZE = 50;
  const INITIAL_PAGE_SIZE = 20; // Reduced initial load to cut image egress
  const [searchQuery, setSearchQuery] = useState('');
  const isSearchingRef = useRef(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSwirlColor, setActiveSwirlColor] = useState(null);
  // MoodSync removed - was causing green background issues
  const [smartSwirls, setSmartSwirls] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [themeFilter, setThemeFilter] = useState(null); // Track active theme filter text
  const [selectedContent, setSelectedContent] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [searchMode, setSearchMode] = useState(null); // 'semantic' | 'keyword' | null
  const [searchMeta, setSearchMeta] = useState(null); // Response metadata (colorQuery, etc.)
  const [deletingContent, setDeletingContent] = useState(null);
  const [contentToDelete, setContentToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // Hover-triggered filters
  const [activeOrgTab, setActiveOrgTab] = useState('all'); // Track active organization tab
  const [selectedCollectionId, setSelectedCollectionId] = useState(null); // Track selected collection
  const [isScrolled, setIsScrolled] = useState(false); // Track scroll position for sticky search bar
  const deletedItemsRef = useRef(new Set()); // Track deleted items to prevent re-adding
  const abortControllerRef = useRef(null);
  const loadMoreDebounceTimeoutRef = useRef(null);
  const activeCollectionRef = useRef(null); // Track active collection to prevent override
  const activeChannelRef = useRef(null); // Track active channel to prevent override
  const chipsRef = useRef([]); // Track current chips to avoid dependency issues
  const filterHoverTimeoutRef = useRef(null); // Timeout for filter hover

  // Dedupe helper function - removes duplicates by ID AND by URL/image_url
  // Keeps the newest item (highest created_at) when duplicates found
  const dedupe = (list) => {
    // First dedupe by ID
    const byId = new Map();
    list.forEach(item => {
      if (!byId.has(item.id)) {
        byId.set(item.id, item);
      }
    });
    
    // Then dedupe by URL (prefer deepsearch.items; otherwise keep newest)
    const byUrl = new Map();
    Array.from(byId.values()).forEach(item => {
      const url = item.url || item.image_url;
      if (!url) {
        // No URL, keep by ID only
        byUrl.set(item.id, item);
        return;
      }
      
      // Normalize URL for comparison (remove query params, trailing slashes)
      const normalizedUrl = url.split('?')[0].replace(/\/+$/, '').toLowerCase();
      
      if (byUrl.has(normalizedUrl)) {
        // Duplicate URL found:
        // - ALWAYS prefer deepsearch.items over instagram_content so reprocess/title updates are reflected
        // - otherwise keep the newer one
        const existing = byUrl.get(normalizedUrl);
        const existingSource = existing?._source || '';
        const newSource = item?._source || '';

        // Prefer unified table records when present
        if (existingSource !== 'deepsearch.items' && newSource === 'deepsearch.items') {
          byUrl.set(normalizedUrl, item);
          return;
        }
        if (existingSource === 'deepsearch.items' && newSource !== 'deepsearch.items') {
          return;
        }

        const existingDate = new Date(existing.created_at || 0);
        const newDate = new Date(item.created_at || 0);
        
        if (newDate > existingDate) {
          byUrl.set(normalizedUrl, item);
        }
      } else {
        byUrl.set(normalizedUrl, item);
      }
    });
    
    return Array.from(byUrl.values());
  };

  // Update chips ref whenever chips change
  useEffect(() => {
    chipsRef.current = chips;
  }, [chips]);

  // Track scroll position for sticky search bar
  useEffect(() => {
    let scrollContainer = null;
    let lastScrollY = -1;
    let lastIsScrolled = null;
    let throttleTimeout = null;
    
    const handleScroll = () => {
      // Clear any pending throttle
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      
      // Throttle to 50ms to prevent excessive updates
      throttleTimeout = setTimeout(() => {
        // Try to find the scrollable main element, fallback to window
        if (!scrollContainer) {
          scrollContainer = document.querySelector('main.overflow-y-auto');
        }
        
        const scrollY = scrollContainer 
          ? scrollContainer.scrollTop 
          : (window.scrollY || window.pageYOffset || document.documentElement.scrollTop);
        
        // Only update if scroll position changed significantly
        if (Math.abs(scrollY - lastScrollY) < 2) {
          return;
        }
        
        lastScrollY = scrollY;
        
        const scrolled = scrollY > 180;
        
        // Only update state if value actually changed
        if (lastIsScrolled !== scrolled) {
          lastIsScrolled = scrolled;
          setIsScrolled(scrolled);
        }
      }, 50); // 50ms throttle
    };

    // Find the scrollable main element
    scrollContainer = document.querySelector('main.overflow-y-auto');
    
    // Call immediately to set initial state
    handleScroll();
    
    // Add scroll listener to the main element if found, otherwise window
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    } else {
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      } else {
        window.removeEventListener('scroll', handleScroll);
        document.removeEventListener('scroll', handleScroll);
      }
    };
  }, []); // Empty deps - only run once on mount

  // Convert chips to structured tokens for API
  const tokens = useMemo(() => {
    return chips.map(c => ({
      type: c.kind,
      value: c.label
    }));
  }, [chips]);

  // Debounce initial search queries (300ms)
  const debouncedTokens = useDebounce(tokens, 300);

  // Fallback search function that queries instagram_content directly
  const fallbackSearch = useCallback(async (searchTokens, page = 0) => {
    // Simplified fallback logic: Only run fallback when explicitly requested (searchTokens.length === 0)
    // If there's an active search (tokens exist), don't load fallback items to avoid confusion
    if (searchTokens.length === 0) {
      // Check if there's an active search happening
      if ((tokens && tokens.length > 0) || (chips && chips.length > 0)) {
        // Don't load fallback items during active search to avoid mixing search results with fallback
        console.log("[Fallback] Skipped - active search in progress");
        return { results: [], hasMore: false, page: 0 };
      }
      
      // Skip fallback if theme filter is active - theme filter manages its own items
      if (activeFilter === 'theme') {
        console.log("[Fallback] Skipped - theme filter is active");
        return { results: [], hasMore: false, page: 0 };
      }

      console.log("[Fallback] Loading all items (no active search)");
    } else {
      // searchTokens.length > 0 means we're doing filtered fallback search
      console.log("[Fallback] Running filtered search with tokens:", searchTokens.length);
    }

    try {
      const { data: session, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        if (import.meta.env.DEV) {
          console.error('❌ Session error in fallbackSearch:', sessionError);
        }
        return { results: [], hasMore: false, page: 0 };
      }
      
      if (!session?.session?.user?.id) {
        if (import.meta.env.DEV) {
          console.warn('⚠️ No user session in fallbackSearch');
        }
        return { results: [], hasMore: false, page: 0 };
      }

      // Extract search terms from tokens
      const searchTerms = searchTokens
        .filter(t => t.type === 'term' || !t.type)
        .map(t => t.value || t)
        .filter(Boolean);

      // Extract channel filters if present (support multiple channels)
      const channelFilters = searchTokens
        .filter(t => t.type === 'channel')
        .map(t => t.value?.trim())
        .filter(Boolean);
      
      // Support single channel filter for backward compatibility
      const channelFilter = channelFilters.length > 0 ? channelFilters[0] : null;

      // Extract content type filter if present
      const typeFilter = searchTokens.find(t => t.type === 'type')?.value;
      
      // Check if favorites filter is active
      // Favorites uses special type='favorites' to distinguish from text search
      const isFavoritesFilter = searchTokens.some(t => 
        t.type === 'favorites' || 
        (t.type === 'filter' && t.value === 'favorites')
      );
      
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:278',message:'Favorites filter detection',data:{isFavoritesFilter,searchTokens},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
      // #endregion
      
      // Always log for debugging filters
      console.log('🔍 [Fallback] Search tokens:', searchTokens);
      console.log('🔍 [Fallback] Extracted typeFilter:', typeFilter);
      console.log('🔍 [Fallback] Channel filters:', channelFilters);
      console.log('🔍 [Fallback] Favorites filter active:', isFavoritesFilter);

      // If favorites filter is active, get favorited item IDs first
      let favoritedItemIds = new Set();
      if (isFavoritesFilter) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:286',message:'Favorites filter activated',data:{userId:session.session.user.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
        // #endregion
        console.log('❤️ [DEBUG] Favorites filter active, querying behavioral_signals...');
        console.log('❤️ [DEBUG] User ID:', session.session.user.id);
        try {
          const { data: favorites, error: favError } = await supabase
            .schema('deepsearch')
            .from('behavioral_signals')
            .select('item_id')
            .eq('user_id', session.session.user.id)
            .eq('interaction_type', 'like');

          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:298',message:'Favorites query completed',data:{hasError:!!favError,errorCode:favError?.code,errorMsg:favError?.message,dataCount:favorites?.length,userId:session.session.user.id},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H2'})}).catch(()=>{});
          // #endregion

          console.log('❤️ [DEBUG] Query completed. Error:', favError, 'Data:', favorites);

          if (favError) {
            console.warn('❤️ Favorites query error:', favError);
            console.warn('❤️ Error code:', favError.code, 'Message:', favError.message);
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:307',message:'Returning empty due to favError',data:{errorCode:favError.code,errorMsg:favError.message},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
            // #endregion
            // Return empty if favorites query fails - don't show all items
            return { results: [], hasMore: false, page: 0 };
          }
          
          if (favorites && favorites.length > 0) {
            favoritedItemIds = new Set(favorites.map(f => f.item_id));
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:315',message:'Favorites found',data:{count:favoritedItemIds.size,itemIds:Array.from(favoritedItemIds)},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
            // #endregion
            console.log(`❤️ Found ${favoritedItemIds.size} favorited items:`, Array.from(favoritedItemIds));
          } else {
            // #region agent log
            fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:321',message:'No favorites - returning empty',data:{favoritesLength:favorites?.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H4'})}).catch(()=>{});
            // #endregion
            console.log('❤️ No favorites found - query returned empty array');
            return { results: [], hasMore: false, page: 0 };
          }
        } catch (error) {
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:328',message:'Exception in favorites query',data:{errorMsg:error.message,errorCode:error.code},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H3'})}).catch(()=>{});
          // #endregion
          console.warn('❤️ Could not load favorites (exception):', error);
          console.warn('❤️ Exception details:', error.message, error.code);
          // Return empty if favorites query fails - don't show all items
          return { results: [], hasMore: false, page: 0 };
        }
      }

      // Stage 2: Unified mapping helper for deepsearch.items → UI content shape
      // STRICT CONTRACT: Title Case channels, consistent field mappings
      const mapDeepsearchItemToUi = (item) => {
        const meta = item?.meta || {};
        const autoTags = item?.auto_tags || {};
        
        // Channel: Strict Title Case, derived from meta.channel or URL hostname
        let channel = meta.channel;
        
        // Normalize to Title Case if present
        if (channel && typeof channel === 'string') {
          const channelLower = channel.toLowerCase();
          // Map known lowercase variants to Title Case
          const channelMap = {
            'instagram': 'Instagram',
            'youtube': 'YouTube',
            'tiktok': 'TikTok',
            'podcast': 'Podcast',
            'web': 'Web',
            'twitter': 'Twitter',
            'linkedin': 'LinkedIn',
            'reddit': 'Reddit',
          };
          channel = channelMap[channelLower] || channel;
        }
        
        // Derive from URL if missing
        if (!channel && item.url) {
          const url = item.url.toLowerCase();
          if (url.includes('instagram.com')) channel = 'Instagram';
          else if (url.includes('youtube.com') || url.includes('youtu.be')) channel = 'YouTube';
          else if (url.includes('tiktok.com')) channel = 'TikTok';
          else if (url.includes('open.spotify.com') || url.includes('podcasts.apple.com')) channel = 'Podcast';
          else if (url.includes('x.com') || url.includes('twitter.com')) channel = 'Twitter';
          else if (url.includes('linkedin.com')) channel = 'LinkedIn';
          else if (url.includes('reddit.com')) channel = 'Reddit';
          else channel = 'Web';
        }
        
        // Default to Web if still missing
        if (!channel) channel = 'Web';

        // ContentType: meta.content_type > source_type > 'link'
        let contentType = meta.content_type;
        if (!contentType && item.source_type) {
          const validTypes = ['video', 'image', 'note', 'pdf', 'link', 'audio', 'carousel', 'reel'];
          contentType = validTypes.includes(item.source_type) ? item.source_type : 'link';
        }
        if (!contentType) contentType = 'link';

        // Thumbnail: first non-null, convert storage paths to public URLs
        let thumbnail_url = item.thumbnail_url || item.image_url || meta.thumbnail_url || meta.thumbnail || null;
        
        // If thumbnail_url is a storage path (not a full URL), convert it to a public URL
        if (thumbnail_url && !thumbnail_url.startsWith('http') && !thumbnail_url.startsWith('data:') && !thumbnail_url.startsWith('/')) {
          // It's likely a storage path like "items/{itemId}.{ext}" or "{userId}/{itemId}/{file}"
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          if (supabaseUrl) {
            // Remove any leading "items/" prefix if present
            const path = thumbnail_url.startsWith('items/') ? thumbnail_url.slice('items/'.length) : thumbnail_url;
            thumbnail_url = `${supabaseUrl}/storage/v1/object/public/items/${path}`;
          }
        }

        // Transcription: meta.transcription?.text only
        const transcription = meta.transcription?.text || null;

        // Topic ID: not set from meta (server query is source of truth)
        const topic_id = null;

        return {
          id: item.id,
          title: item.title || null,
          description: item.description || null,
          url: item.url || null,
          thumbnail_url: thumbnail_url,
          image_url: item.image_url || thumbnail_url || null,
          created_at: item.created_at || null,
          source_type: item.source_type,
          contentType: contentType,
          channel: channel, // Guaranteed Title Case
          auto_tags: autoTags || { topics: [] },
          primary_tags: item.primary_tags || [],
          extracted_text: item.extracted_text || item.raw_text || null,
          analysis_data: meta.analysis_data || meta.ai_analysis || autoTags || {},
          transcription: transcription,
          meta: meta, // Preserved
          status: item.status,
          _source: 'deepsearch.items', // Always
          topic_id: topic_id,
          // Preserve additional fields for compatibility
          primary_color: item.primary_color || null,
          color_mood: item.color_mood || null,
          color_palette: item.color_palette || null,
        };
      };

      const results = [];
      
      // 2. Query deepsearch.items (UNIFIED TABLE - all content including mobile shares)
      // CHANGED: Always query deepsearch.items, not just when channelFilters is empty
      // This table now contains mobile app shares (Instagram, YouTube, TikTok) AND uploads
      try {
        console.log('🔍 [Fallback] Querying unified deepsearch.items table, page:', page);
        
        // Stage 3: Check for ?showDuplicates=1 query param (debug mode)
        const urlParams = new URLSearchParams(window.location.search);
        const showDuplicates = urlParams.get('showDuplicates') === '1';
        
        let deepsearchQuery = supabase
          .schema('deepsearch')
          .from('items')
          .select('id, title, description, url, image_url, thumbnail_url, source_type, source_table, file_metadata, auto_tags, primary_tags, extracted_text, primary_color, color_mood, color_palette, meta, created_at, status')
          .eq('user_id', session.session.user.id)
          .not('url', 'like', 'pending://%');
        
        // Stage 3: Hide duplicates by default (unless showDuplicates=1)
        if (!showDuplicates) {
          deepsearchQuery = deepsearchQuery.is('duplicate_of', null);
        }
        
        // Apply favorites filter to deepsearch.items query
        if (isFavoritesFilter && favoritedItemIds.size > 0) {
          deepsearchQuery = deepsearchQuery.in('id', Array.from(favoritedItemIds));
        }

        // Apply type filter for deepsearch.items
        // Note: We filter client-side because items can have source_type='url' but are actually videos
        // The database filter is just an optimization, real filtering happens after
        let typeFilterClientSide = null;
        if (typeFilter) {
          const typeMap = {
            videos: 'video',
            images: 'image',
            notes: 'note',
            articles: 'link',
          };
          typeFilterClientSide = typeMap[typeFilter];
          console.log('🔍 [Fallback] Type filter will be applied client-side:', { typeFilter, mappedType: typeFilterClientSide });
        } else {
          console.log('ℹ️ [Fallback] No type filter - querying all types');
        }

        // Range-based pagination: use range() instead of limit
        // Use smaller initial page size to reduce image egress on first load
        const pageSize = page === 0 ? INITIAL_PAGE_SIZE : PAGE_SIZE;
        const from = page === 0 ? 0 : (INITIAL_PAGE_SIZE + (page - 1) * PAGE_SIZE);
        const to = from + pageSize - 1;
        console.log('📄 [Fallback] Pagination range:', { from, to, page, pageSize });

          const { data: deepsearchData, error: deepsearchError } = await deepsearchQuery
            .order('created_at', { ascending: false })
            .range(from, to);

          if (!deepsearchError && deepsearchData) {
            // #region agent log
            try {
              const s = deepsearchData?.[0];
              const sample = s
                ? {
                    id: s.id,
                    title: typeof s.title === 'string' ? s.title.slice(0, 80) : s.title,
                    thumbnail_url:
                      typeof s.thumbnail_url === 'string'
                        ? s.thumbnail_url.slice(0, 140)
                        : s.thumbnail_url,
                    image_url:
                      typeof s.image_url === 'string' ? s.image_url.slice(0, 140) : s.image_url,
                    source_type: s.source_type,
                    source_table: s.source_table,
                    hasAutoTopics: Array.isArray(s.auto_tags?.topics),
                    autoTopicCount: Array.isArray(s.auto_tags?.topics) ? s.auto_tags.topics.length : 0,
                    hasMetaAiSummary: !!(s.meta && s.meta.ai_analysis && s.meta.ai_analysis.summary),
                  }
                : null;
            } catch {}
            // #endregion agent log
            
            // Map all items through unified mapping function
            let mappedData = deepsearchData.map(mapDeepsearchItemToUi);
            
            // No client-side collection filtering - server query is source of truth
            
            // Apply channel filter client-side using mapped channel value
            if (channelFilters.length > 0) {
              console.log('🔍 Applying channel filters client-side:', channelFilters);
              mappedData = mappedData.filter(item => channelFilters.includes(item.channel));
              console.log('✅ Channel filter(s) applied, results:', mappedData.length);
            }
            
            // Apply type filter client-side (checks source_type and meta.content_type)
            if (typeFilterClientSide) {
              mappedData = mappedData.filter(item => {
                // Check direct source_type match
                if (item.source_type === typeFilterClientSide) return true;
                // Check contentType (which is meta.content_type || source_type)
                if (item.contentType === typeFilterClientSide) return true;
                // For image filter: include 'carousel' and 'screenshot'
                if (typeFilterClientSide === 'image' && (item.source_type === 'screenshot' || item.source_type === 'carousel')) {
                  return true;
                }
                return false;
              });
              console.log(`📊 [Fallback] Type filter applied: ${deepsearchData.length} total, ${mappedData.length} matching '${typeFilterClientSide}'`);
            }
            
            console.log(`✅ [Fallback] Loaded ${mappedData.length} items from deepsearch.items`);
            if (mappedData.length > 0) {
              console.log('📋 [Fallback] Sample items:', mappedData.slice(0, 3).map(item => ({
                id: item.id,
                title: item.title?.substring(0, 50),
                source_type: item.source_type,
                channel: item.channel,
                status: item.status,
                collection_id: item.topic_id || 'NULL',
              })));
            }
            mappedData.forEach(item => {
              results.push(item);
            });
          } else if (deepsearchError) {
            console.error('❌ [Fallback] Error loading deepsearch.items:', deepsearchError);
            // Don't throw - just log and continue with empty results
            // This prevents infinite loading
          }
        } catch (err) {
          console.warn('⚠️ [Fallback] Error querying deepsearch.items:', err);
        }

      // REMOVED: Legacy instagram_content and swirl_items queries
      // All content is now in deepsearch.items unified table

      // Log success
      if (import.meta.env.DEV) {
        const itemCount = results.length;
        console.log(`✅ [Fallback] Query successful: ${itemCount} items from deepsearch.items (unified table)`);
      }

      // Client-side filtering: Search across multiple fields if we have search terms
      // Also filter by favorites if favorites filter is active (in case some items weren't caught by DB query)
      let filteredData = results || [];
      
      // Apply favorites filter client-side as well (for safety)
      if (isFavoritesFilter && favoritedItemIds.size > 0) {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:577',message:'Client-side favorites filter',data:{beforeCount:filteredData.length,favoritedCount:favoritedItemIds.size},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5'})}).catch(()=>{});
        // #endregion
        filteredData = filteredData.filter(item => favoritedItemIds.has(item.id));
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/d63f98f2-a476-45f2-8f18-652bfd36f226',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'SwirlspacePage.jsx:582',message:'After client-side favorites filter',data:{afterCount:filteredData.length},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H5'})}).catch(()=>{});
        // #endregion
        console.log(`❤️ Filtered to ${filteredData.length} favorited items`);
      }
      
      // Note: Type and channel filtering already applied above during mapping
      // This section only handles text search now
      
      if (searchTerms.length > 0 && !isFavoritesFilter) {
        const searchLower = searchTerms.join(' ').toLowerCase();
        
        filteredData = (results || []).filter(item => {
          // Search in title
          if (item.title?.toLowerCase().includes(searchLower)) return true;
          
          // Search in description
          if (item.description?.toLowerCase().includes(searchLower)) return true;
          
          // Search in tags (analysis_data.tags is an array for instagram_content)
          if (item.analysis_data?.tags) {
            const tagsArray = Array.isArray(item.analysis_data.tags) 
              ? item.analysis_data.tags 
              : [];
            if (tagsArray.some(tag => tag.toLowerCase().includes(searchLower))) {
              return true;
            }
          }
          
          // Search in auto_tags.topics (for deepsearch.items)
          if (item.auto_tags?.topics) {
            const topicsArray = Array.isArray(item.auto_tags.topics) 
              ? item.auto_tags.topics 
              : [];
            if (topicsArray.some(topic => topic.toLowerCase().includes(searchLower))) {
              return true;
            }
          }
          
          // Search in primary_tags (for deepsearch.items)
          if (item.primary_tags) {
            const primaryTagsArray = Array.isArray(item.primary_tags) 
              ? item.primary_tags 
              : [];
            if (primaryTagsArray.some(tag => tag.toLowerCase().includes(searchLower))) {
              return true;
            }
          }
          
          // Search in OCR text (extracted_text from deepsearch.items)
          if (item.extracted_text && typeof item.extracted_text === 'string') {
            if (item.extracted_text.toLowerCase().includes(searchLower)) return true;
          }
          
          // Search in OCR text from meta.analysis_data (if stored there)
          if (item.analysis_data?.ocr?.text) {
            if (item.analysis_data.ocr.text.toLowerCase().includes(searchLower)) return true;
          }
          
          // Search in transcription
          if (item.transcription) {
            if (typeof item.transcription === 'string') {
              if (item.transcription.toLowerCase().includes(searchLower)) return true;
            } else if (item.transcription.full_transcript?.toLowerCase().includes(searchLower)) {
              return true;
            } else if (item.transcription.text?.toLowerCase().includes(searchLower)) {
              return true;
            }
          }
          
          // Search for colors (if searching for color names like "blue", "red", etc.)
          const colorKeywords = ['blue', 'red', 'green', 'yellow', 'purple', 'orange', 'pink', 'brown', 'black', 'white', 'gray', 'grey', 'warm', 'cool'];
          const isColorSearch = colorKeywords.some(color => searchLower.includes(color));
          if (isColorSearch) {
            // Check primary_color
            if (item.primary_color && item.primary_color.toLowerCase().includes(searchLower)) return true;
            // Check color_mood
            if (item.color_mood && item.color_mood.toLowerCase().includes(searchLower)) return true;
            // Check color_palette
            if (item.color_palette?.colors) {
              const colorsArray = Array.isArray(item.color_palette.colors) ? item.color_palette.colors : [];
              if (colorsArray.some(color => color.toLowerCase().includes(searchLower))) return true;
            }
            // Check meta.analysis_data.colors
            if (item.analysis_data?.colors?.primaryColor) {
              if (item.analysis_data.colors.primaryColor.toLowerCase().includes(searchLower)) return true;
            }
          }
          
          return false;
        });
        
        // Limit results after filtering
        filteredData = filteredData.slice(0, limit);
        
        if (import.meta.env.DEV) {
          console.log(`🔍 Search "${searchTerms.join(' ')}": Found ${filteredData.length} items out of ${results?.length || 0}`);
        }
      }

      // Sort by created_at (most recent first)
      filteredData.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateB - dateA;
      });

      // Note: Results are already mapped via mapDeepsearchItemToUi earlier
      // No additional mapping needed - items are ready for UI
      const mappedResults = filteredData;

      // Determine if there are more pages: if we got a full page, there might be more
      // Account for initial page size when checking if more results available
      const expectedPageSize = page === 0 ? INITIAL_PAGE_SIZE : PAGE_SIZE;
      const hasMore = mappedResults.length >= expectedPageSize;

      // Always log results for debugging
      console.log('📦 Fallback search results:', {
        rawCount: results?.length || 0,
        filteredCount: filteredData.length,
        mappedCount: mappedResults.length,
        page: page,
        hasMore: hasMore,
        typeFilter: typeFilter,
        isFavoritesFilter: isFavoritesFilter,
        instagramCount: filteredData.filter(i => i._source === 'instagram_content').length,
        deepsearchCount: filteredData.filter(i => i._source === 'deepsearch.items').length,
        sample: mappedResults[0]
      });

      return { results: mappedResults, hasMore, page };
    } catch (error) {
      console.error('Fallback search failed:', error);
      return { results: [], hasMore: false, page: 0 };
    }
  }, [tokens, chips, activeFilter]);

  // Search function - Phase 3: Unified search pipeline
  // Now calls deepsearchService.search() instead of hybridSearch()
  const performSearch = useCallback(async (searchTokens, cursor = null, isLoadMore = false) => {
    console.log("[SEARCH] Starting search:", { tokens: searchTokens.length, cursor: cursor ? 'yes' : 'no', isLoadMore });

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Clear previous items when starting a NEW search (not pagination)
    if (!cursor) {
      setItems([]);
    }

    setLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      // Phase 3: Use unified search() method - single entry point
      // Use smaller limit for initial load (no cursor) to reduce image egress
      const searchLimit = cursor ? 60 : 20; // Initial load: 20, pagination: 60
      const response = await deepsearchService.search(searchTokens, {
        limit: searchLimit,
        cursor,
      });

      if (abortController.signal.aborted) {
        return;
      }

      const latency = Date.now() - startTime;

      console.log("[SEARCH] Response:", {
        ok: response.ok,
        results: response.results?.length || 0,
        searchMode: response.searchMode,
        latency: `${latency}ms`
      });

      // Handle response based on SearchResponse type from search.ts
      if (!response || !response.ok) {
        throw new Error(response?.error || 'Search failed');
      }

      const newResults = Array.isArray(response.results) ? response.results : [];

      // Update items with deduplication
      if (cursor) {
        // Pagination: append new results
        setItems(prev => dedupe([...prev, ...newResults]));
      } else {
        // New search: replace all results
        setItems(dedupe(newResults));
      }

      // Update pagination state
      setNextCursor(response.nextCursor || null);
      
      // Update search mode (semantic, keyword, or empty)
      setSearchMode(response.searchMode || null);
      
      // Store response metadata (for color fallback messaging, etc.)
      setSearchMeta(response.meta || null);

      // Store debug results
      if (typeof window !== 'undefined') {
        window.lastDeepsearchResults = newResults;
      }

      // Analytics logging (gracefully handle if deepsearch.queries doesn't exist)
      try {
        const { data: session } = await supabase.auth.getSession();
        if (session?.session?.user?.id) {
          const queryText = searchTokens.map(t => t.value || t).join(' -> ');
          const { error: analyticsError } = await supabase.schema('deepsearch').from('queries').insert({
            user_id: session.session.user.id,
            query: queryText,
            token_count: searchTokens.length,
            results_count: newResults.length,
            latency_ms: latency,
          });
          
          // Only log if it's not a missing table error
          if (analyticsError && analyticsError.code !== 'PGRST116' && analyticsError.code !== 'PGRST205' && !analyticsError.message?.includes('does not exist')) {
            if (import.meta.env.DEV) {
              console.warn('Analytics logging failed:', analyticsError);
            }
          }
        }
      } catch (analyticsError) {
        // Silently ignore missing table errors
        if (import.meta.env.DEV && analyticsError.code !== 'PGRST116' && analyticsError.code !== 'PGRST205') {
          console.warn('Analytics logging failed:', analyticsError);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }

      console.error('[SEARCH] Error:', err.message);
      setError('Search failed. Please try again.');
      if (!isLoadMore) {
        setItems([]);
        setNextCursor(null);
      }
    } finally {
      if (!abortControllerRef.current?.signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Listen for clear filters event (when switching to All tab)
  useEffect(() => {
    // Skip if theme filter is active
    if (activeFilter === 'theme') {
      console.log("[ClearFilters] Skipped - theme filter is active");
      return;
    }
    
    if ((tokens && tokens.length > 0) || (chips && chips.length > 0)) {
      console.log("[HARD BLOCK] Fallback prevented because tokens exist");
      return;
    }

    const handleClearFilters = () => {
      // Clear collection and channel filters
      activeCollectionRef.current = null;
      activeChannelRef.current = null;
      
      // Reload all items immediately (respecting chips/filters but NOT collection)
      // Use chipsRef to get current chips without causing re-render loops
      const currentTokens = chipsRef.current.map(c => ({
        type: c.kind,
        value: c.label
      }));
      
      console.log("[Fallback] Running normally - clear filters event");
      setLoading(true);
      fallbackSearch(currentTokens, 0).then(result => {
        const filteredResults = (result.results || []).filter(
          item => !deletedItemsRef.current.has(item.id)
        );
        setItems(dedupe(filteredResults));
        setNextCursor(result.nextCursor || null);
        setError(null);
        setLoading(false);
      }).catch(err => {
        console.error('Error reloading all items:', err);
        setError(null);
        setItems([]);
        setLoading(false);
      });
    };
    
    window.addEventListener('clearActiveFilters', handleClearFilters);
    return () => {
      window.removeEventListener('clearActiveFilters', handleClearFilters);
    };
  }, [fallbackSearch, activeFilter]); // Include activeFilter to properly skip when theme is active

  // Sync activeChannelRef when channel chips are added/removed
  useEffect(() => {
    // Skip if theme filter is active
    if (activeFilter === 'theme') {
      return;
    }
    
    if ((tokens && tokens.length > 0) || (chips && chips.length > 0)) {
      console.log("[HARD BLOCK] Fallback prevented because tokens exist");
      return;
    }

    const channelChip = chips.find(c => c.kind === 'channel');
    if (channelChip) {
      // Channel chip exists - sync the ref
      if (activeChannelRef.current !== channelChip.label) {
        activeChannelRef.current = channelChip.label;
      }
    } else {
      // No channel chip - clear the ref
      if (activeChannelRef.current !== null) {
        activeChannelRef.current = null;
        
        console.log("[Fallback] Running normally - channel filter removed");
        setLoading(true);
        const remainingTokens = chips.map(c => ({
          type: c.kind,
          value: c.label
        }));
        fallbackSearch(remainingTokens, 0).then(result => {
          const filteredResults = (result.results || []).filter(
            item => !deletedItemsRef.current.has(item.id)
          );
          setItems(dedupe(filteredResults));
          setNextCursor(result.nextCursor || null);
          setError(null);
          setLoading(false);
        }).catch(err => {
          console.error('Error reloading after channel filter removal:', err);
          setError(null);
          setLoading(false);
        });
      }
    }
  }, [chips, fallbackSearch, activeFilter]);

  // Initial search when tokens change (debounced)
  useEffect(() => {
    if (!FLAGS.SWIRLSPACE_ENABLED) {
      return;
    }
    
    // Skip this useEffect if theme filter is active - theme filter manages its own items
    if (activeFilter === 'theme') {
      console.log("[Search] Skipped - theme filter is active");
      return;
    }

    // Extract search query from tokens (include ALL searchable types: term, color, etc.)
    const searchQuery = debouncedTokens
      .filter(t => !t.type || t.type === 'term' || t.type === 'color' || t.type === 'tag')
      .map(t => t.value || t)
      .filter(Boolean)
      .join(' ');

    // Update searchQuery state
    setSearchQuery(searchQuery);

    // Log the parsed tokens
    console.log("Search tokens:", debouncedTokens);
    
    // Add guard so the search effect returns early when isSearching is already true
    if (isSearchingRef.current) {
      return;
    }

    // If the search query is empty AND we have tokens (meaning user cleared search), clear results
    // But if we have NO tokens at all, this is initial load - allow it to proceed
    // IMPORTANT: Check if ALL tokens are filters (channel, collection, type), not search tokens
    // Include 'favorites' as a valid search token that should trigger a search
    const hasSearchTokens = debouncedTokens.some(t => 
      !t.type || 
      t.type === 'term' || 
      t.type === 'color' || 
      t.type === 'tag' || 
      t.type === 'favorites' ||  // Favorites triggers behavioral_signals search
      t.type === 'type'          // Type filters (videos, images, etc.)
    );
    if (debouncedTokens.length > 0 && !hasSearchTokens && (!searchQuery || searchQuery.trim().length === 0)) {
      // User had a search but cleared it - clear results
      setItems([]);
      isSearchingRef.current = false;
      setLoading(false);
      return;
    }
    
    // Stop calling deepsearch with an empty array only if we have tokens but they're all filters
    // If debouncedTokens.length === 0, this is initial load - let it proceed

    // Check if we're on the "All" tab (via event listener or state)
    // Only apply collection/channel filters if we're NOT on the All tab
    const isOnAllTab = !activeCollectionRef.current && !activeChannelRef.current;
    
    // Don't override if a collection or channel is actively selected AND we're not on All tab
    if ((activeCollectionRef.current || activeChannelRef.current) && !isOnAllTab) {
      // Only update if there are search tokens (chips)
      if (debouncedTokens.length > 0) {
        // Apply search tokens but keep collection/channel filter
        const collectionId = activeCollectionRef.current;
        const channelFilter = activeChannelRef.current;
        
        // Merge tokens with active filters
        const tokensWithFilters = [...debouncedTokens];
        if (channelFilter && !tokensWithFilters.find(t => t.type === 'channel' && t.value === channelFilter)) {
          tokensWithFilters.push({ type: 'channel', value: channelFilter });
        }
        
        if (collectionId) {
          // Search within collection
          // Prevent the fallback search from running twice
          if (isSearchingRef.current) return;
          isSearchingRef.current = true;
          
          fallbackSearch(tokensWithFilters, 0).then(result => {
            const filteredResults = (result.results || []).filter(
              item => !deletedItemsRef.current.has(item.id)
            );
            setItems(dedupe(filteredResults));
            setNextCursor(result.nextCursor || null);
            setError(null);
            isSearchingRef.current = false;
          }).catch(err => {
            console.error('Search error:', err);
            setError(null);
            isSearchingRef.current = false;
          });
        } else if (channelFilter) {
          // Search within channel
          // Prevent the fallback search from running twice
          if (isSearchingRef.current) return;
          isSearchingRef.current = true;
          
          fallbackSearch(tokensWithFilters, 0).then(result => {
            const filteredResults = (result.results || []).filter(
              item => !deletedItemsRef.current.has(item.id)
            );
            setItems(dedupe(filteredResults));
            setNextCursor(result.nextCursor || null);
            setError(null);
            isSearchingRef.current = false;
          }).catch(err => {
            console.error('Search error:', err);
            setError(null);
            isSearchingRef.current = false;
          });
        }
      }
      return; // Don't run default load
    }
    
    // Clear active filters when switching to All tab
    if (isOnAllTab) {
      activeCollectionRef.current = null;
      activeChannelRef.current = null;
    }

    if (debouncedTokens.length > 0) {
      // Check if favorites filter is active - needs special handling via fallbackSearch
      const isFavoritesFilter = debouncedTokens.some(t => t.type === 'favorites');
      
      // Check if type filter is active (videos, images, etc.) - needs fallbackSearch
      const isTypeFilter = debouncedTokens.some(t => t.type === 'type');
      
      // Check if there are actual search terms (not just filters)
      const hasSearchTerms = debouncedTokens.some(t => 
        t.type === 'term' || t.type === 'color' || t.type === 'tag'
      );
      
      if (isFavoritesFilter || (isTypeFilter && !hasSearchTerms)) {
        // Use fallbackSearch for favorites OR type-only filters (queries db directly)
        if (isSearchingRef.current) return;
        isSearchingRef.current = true;
        setLoading(true);
        
        console.log('[Filter] Loading items via fallbackSearch:', { isFavoritesFilter, isTypeFilter });
        fallbackSearch(debouncedTokens, 0).then(result => {
          const filteredResults = (result.results || []).filter(
            item => !deletedItemsRef.current.has(item.id)
          );
          setItems(dedupe(filteredResults));
          setNextCursor(result.nextCursor || null);
          setError(null);
          setLoading(false);
          isSearchingRef.current = false;
        }).catch(err => {
          console.error('Filter search error:', err);
          setItems([]);
          setLoading(false);
          isSearchingRef.current = false;
        });
      } else {
        // Use vector search for regular queries (with or without type filters)
        performSearch(debouncedTokens, null, false);
      }
    } else {
      // No search terms - load all content
      // BUT skip if theme filter is active (theme filter manages its own items)
      if (activeFilter === 'theme') {
        console.log("[Search] Skipped fallback - theme filter is active");
        return;
      }
      
      // Prevent the fallback search from running twice
      if (isSearchingRef.current) return;
      isSearchingRef.current = true;
      
      if (!loading) {
        setLoading(true);
      }
      
      setCurrentPage(0);
      
      // Fetch total count once (in parallel)
      if (totalItemCount === null && user?.id) {
        supabase
          .schema('deepsearch')
          .from('items')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .not('url', 'like', 'pending://%')
          .then(({ count }) => {
            if (count !== null) setTotalItemCount(count);
          })
          .catch(err => console.warn('Failed to fetch total count:', err));
      }
      
      fallbackSearch([], 0).then(result => {
        // Double-check theme filter wasn't activated while we were fetching
        if (activeFilter === 'theme') {
          console.log("[Search] Skipped setItems - theme filter became active");
          isSearchingRef.current = false;
          setLoading(false);
          return;
        }
        
        if (import.meta.env.DEV) {
          console.log('✅ Loaded initial items:', result.results?.length || 0);
          if (result.results?.length > 0) {
            console.log('Sample item:', result.results[0]);
          } else {
            console.warn('⚠️ No items loaded despite query succeeding');
          }
        }
        // Filter out deleted items to prevent them from reappearing
        const filteredResults = (result.results || []).filter(
          item => !deletedItemsRef.current.has(item.id)
        );
        if (filteredResults.length !== result.results?.length) {
          console.log(`🗑️ Filtered out ${(result.results?.length || 0) - filteredResults.length} deleted items`);
        }
        setItems(dedupe(filteredResults));
        setCurrentPage(0);
        // Set nextCursor based on hasMore for intersection observer
        setNextCursor(result.hasMore ? 'has-more' : null);
        setError(null);
        setLoading(false);
        isSearchingRef.current = false;
      }).catch(err => {
        console.error('❌ Error loading all content:', err);
        console.error('Error stack:', err.stack);
        setError(null);
        // Don't set items to empty if theme filter is active
        if (activeFilter !== 'theme') {
          setItems(dedupe([]));
        }
        setLoading(false);
        isSearchingRef.current = false;
      });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isSearchingRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTokens, performSearch, fallbackSearch, activeFilter]);

  // Infinite scroll handler - instant loading, no debounce for better UX
  const handleLoadMore = useCallback(() => {
    // For search queries, use nextCursor
    if (tokens.length > 0) {
      if (!nextCursor || loading) return;
      performSearch(tokens, nextCursor, true);
      return;
    }
    
    // For fallback (no search), use page-based pagination
    if (loading) return;
    
    const nextPage = currentPage + 1;
    setLoading(true);
    
    fallbackSearch([], nextPage).then(result => {
      if (result.results && result.results.length > 0) {
        setItems(prev => dedupe([...prev, ...result.results]));
        setCurrentPage(nextPage);
        // Set nextCursor based on hasMore for intersection observer
        setNextCursor(result.hasMore ? 'has-more' : null);
      } else {
        setNextCursor(null);
      }
      setLoading(false);
    }).catch(err => {
      console.error('❌ Error loading more items:', err);
      setLoading(false);
    });
  }, [nextCursor, loading, tokens, currentPage, performSearch, fallbackSearch]);

  // Infinite scroll intersection observer
  useEffect(() => {
    if (!FLAGS.SWIRLSPACE_ENABLED) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // For search queries, check nextCursor; for fallback, always allow if not loading
        const shouldLoad = tokens.length > 0 
          ? (nextCursor && !loading)
          : (nextCursor !== null && !loading); // For fallback, nextCursor='has-more' means more pages
        if (entries[0].isIntersecting && shouldLoad) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const sentinel = document.getElementById('scroll-sentinel');
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
      if (loadMoreDebounceTimeoutRef.current) {
        clearTimeout(loadMoreDebounceTimeoutRef.current);
      }
    };
  }, [nextCursor, loading, handleLoadMore]);

  // MoodSync removed - Smart Swirls loading moved elsewhere if needed

  // Scroll to organization layer helper
  const scrollToOrganizationLayer = () => {
    const orgLayer = document.getElementById('organization-layer');
    if (orgLayer) {
      orgLayer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Removed organization:filterItem handler - Recent items now open modal instead

  // Listen for content added events to refresh the main items list
  useEffect(() => {
    // Skip if theme filter is active
    if (activeFilter === 'theme') {
      return;
    }
    
    if ((tokens && tokens.length > 0) || (chips && chips.length > 0)) {
      console.log("[HARD BLOCK] Fallback prevented because tokens exist");
      return;
    }

    const handleContentAdded = () => {
      // Refresh items if no search is active (empty chips)
      console.log("[Fallback] Running normally - refreshing after content added");
      setLoading(true);
      fallbackSearch([], 0).then(result => {
        // Filter out deleted items
        const filteredResults = (result.results || []).filter(
          item => !deletedItemsRef.current.has(item.id)
        );
        setItems(dedupe(filteredResults));
        setNextCursor(result.nextCursor || null);
        setError(null);
      }).catch(err => {
        console.error('Error refreshing content:', err);
      }).finally(() => {
        setLoading(false);
      });
    };

    window.addEventListener('contentAdded', handleContentAdded);
    return () => {
      window.removeEventListener('contentAdded', handleContentAdded);
    };
  }, [chips.length, fallbackSearch, activeFilter]);

  // Real-time subscription for status updates (both instagram_content and deepsearch.items)
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔔 [Swirlspace] Setting up real-time subscriptions for status updates');

    // Subscribe to instagram_content updates
    const instagramChannel = supabase
      .channel(`swirlspace-instagram-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'instagram_content',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        console.log('🔄 [Swirlspace] Instagram content updated:', payload.new.id, 'status:', payload.new.analysis_status);
        // Update the item in state if it exists
        setItems(prev => dedupe(prev.map(item => {
          if (item.id === payload.new.id && (!item._source || item._source === 'instagram_content')) {
            return {
              ...item,
              analysis_status: payload.new.analysis_status,
              status: payload.new.analysis_status,
              analysis_data: payload.new.analysis_data,
            };
          }
          return item;
        })));
      })
      .subscribe();

    // Subscribe to deepsearch.items updates (INSERT, UPDATE, and DELETE)
    const deepsearchChannel = supabase
      .channel(`swirlspace-deepsearch-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'deepsearch',
        table: 'items',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        if (payload.eventType === 'INSERT') {
          console.log('📥 [Swirlspace] New deepsearch item:', payload.new.id);
          // Add new item to the beginning of the list
          const newItem = {
            ...payload.new,
            _source: 'deepsearch.items',
            analysis_status: payload.new.status,
            analysis_data: payload.new.meta?.analysis_data,
          };
          setItems(prev => {
            // Check if item already exists (avoid duplicates)
            const exists = prev.some(item => item.id === payload.new.id);
            if (exists) return prev;
            return dedupe([newItem, ...prev]);
          });
        } else if (payload.eventType === 'UPDATE') {
          console.log('🔄 [Swirlspace] Deepsearch item updated:', payload.new.id, 'status:', payload.new.status);
          // Update the item in state if it exists
          setItems(prev => dedupe(prev.map(item => {
            if (item.id === payload.new.id && item._source === 'deepsearch.items') {
              return {
                ...item,
                status: payload.new.status,
                analysis_status: payload.new.status,
                extracted_text: payload.new.extracted_text,
                auto_tags: payload.new.auto_tags,
                primary_tags: payload.new.primary_tags,
                primary_color: payload.new.primary_color,
                color_mood: payload.new.color_mood,
                color_palette: payload.new.color_palette,
                meta: payload.new.meta,
                analysis_data: payload.new.meta?.analysis_data,
              };
            }
            return item;
          })));
        } else if (payload.eventType === 'DELETE') {
          console.log('🗑️ [Swirlspace] Deepsearch item deleted:', payload.old.id);
          setItems(prev => prev.filter(item => item.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => {
      console.log('🔕 [Swirlspace] Cleaning up real-time subscriptions');
      instagramChannel.unsubscribe();
      deepsearchChannel.unsubscribe();
    };
  }, [user?.id]);

  // Handle filter clicks from FilterLayer
  const handleFilterLayerClick = (filterData) => {
    if (filterData.type === 'filter') {
      // Handle Smart Swirls specially - scroll to organization layer
      if (filterData.value === 'smart_swirls') {
        setActiveFilter('smart_swirls');
        scrollToOrganizationLayer();
        return;
      }
      
      // Handle standard filters (favorites, videos, images, etc.)
      setActiveFilter(filterData.value);
      
      const filterMap = {
        favorites: { type: 'favorites', label: 'favorites', value: 'favorites' }, // Special type for favorites
        videos: { type: 'type', label: 'videos', value: 'videos' },
        images: { type: 'type', label: 'images', value: 'images' },
        notes: { type: 'type', label: 'notes', value: 'notes' },
        articles: { type: 'type', label: 'articles', value: 'articles' },
        books: { type: 'type', label: 'books', value: 'books' },
        podcasts: { type: 'type', label: 'podcasts', value: 'podcasts' },
      };

      const filterValue = filterMap[filterData.value];
      if (filterValue) {
        // For type filters (videos, images, notes, etc.), remove ALL other type filters first
        // This makes them mutually exclusive - only one type filter can be active at a time
        let filteredChips;
        if (filterValue.type === 'type') {
          // Remove all existing type filters
          filteredChips = chips.filter(c => c.kind !== 'type');
          console.log('🔄 Clearing other type filters, setting:', filterValue.label);
        } else {
          // For non-type filters (like favorites), just toggle that specific one
          filteredChips = chips.filter(c => 
            !(c.kind === filterValue.type && c.label === filterValue.label)
          );
        }
        
        const newChip = {
          id: crypto.randomUUID(),
          label: filterValue.label,
          kind: filterValue.type
        };
        
        // Check if this filter is already active (toggle off if clicking the same one)
        const isAlreadyActive = chips.some(c => 
          c.kind === filterValue.type && c.label === filterValue.label
        );
        
        if (isAlreadyActive && filterValue.type === 'type') {
          // Toggle off - remove the filter
          setChips(filteredChips);
          setActiveFilter(null);
          console.log('🔄 Toggled off type filter:', filterValue.label);
        } else {
          // Add the new filter
          setChips([...filteredChips, newChip]);
          console.log('✅ Set type filter:', filterValue.label);
        }
      }
    } else if (filterData.type === 'channel') {
      // Handle channel filter - support multiple channel selections
      setActiveFilter({ type: 'channel', value: filterData.value });
      
      // Check if this channel is already selected
      const existingChannelChip = chips.find(c => c.kind === 'channel' && c.label === filterData.value);
      
      let updatedChips;
      if (existingChannelChip) {
        // Remove channel if already selected (toggle off)
        updatedChips = chips.filter(c => !(c.kind === 'channel' && c.label === filterData.value));
        console.log('🔍 Channel filter removed:', filterData.value);
      } else {
        // Add channel (allow multiple)
        const newChip = {
          id: crypto.randomUUID(),
          label: filterData.value,
          kind: 'channel'
        };
        updatedChips = [...chips, newChip];
        console.log('🔍 Channel filter added:', filterData.value);
      }
      
      setChips(updatedChips);
      
      // Get all selected channels
      const selectedChannels = updatedChips
        .filter(c => c.kind === 'channel')
        .map(c => c.label);
      
      // Update active channel ref (use first channel for backward compatibility)
      activeChannelRef.current = selectedChannels.length > 0 ? selectedChannels[0] : null;
      activeCollectionRef.current = null;
      
      // Build channel tokens for search
      const channelTokens = selectedChannels.map(ch => ({ type: 'channel', value: ch }));
      
      console.log('🔍 Selected channels:', selectedChannels);
      console.log('🔍 Triggering search with channel tokens:', channelTokens);
      setLoading(true);
      
      // If no channels selected, show all content
      if (selectedChannels.length === 0) {
        console.log("[Fallback] Running normally - no channel filters");
        fallbackSearch([], 0).then(result => {
          const filteredResults = (result.results || []).filter(
            item => !deletedItemsRef.current.has(item.id)
          );
          setItems(dedupe(filteredResults));
          setNextCursor(result.nextCursor || null);
          setError(null);
          setLoading(false);
        }).catch(err => {
          console.error('❌ Search error:', err);
          setError(null);
          setLoading(false);
        });
      } else {
        console.log("[Fallback] Running normally - with channel filters");
        fallbackSearch(channelTokens, 0).then(result => {
          console.log('✅ Channel filter search completed:', {
            totalResults: result.results?.length || 0,
            channels: selectedChannels
          });
          const filteredResults = (result.results || []).filter(
            item => !deletedItemsRef.current.has(item.id)
          );
          console.log('📊 Filtered results (after deleted items):', filteredResults.length);
          console.log('📊 Sample items:', filteredResults.slice(0, 3).map(i => ({ id: i.id, channel: i.channel, title: i.title })));
          setItems(dedupe(filteredResults));
          setNextCursor(result.nextCursor || null);
          setError(null);
          setLoading(false);
        }).catch(err => {
          console.error('❌ Channel filter search error:', err);
          setError(null);
          setLoading(false);
        });
      }
    }
  };


  // Handle collection click from OrganizationLayer
  const handleCollectionClick = async (collection) => {
    // DON'T switch to All tab - stay on Collections tab
    // Load content from this collection but keep user on Collections tab
    try {
      // Clear items IMMEDIATELY to prevent showing stale content
      setItems([]);
      setLoading(true);
      setActiveFilter(null);
      
      // Set active collection to prevent useEffect from overriding
      activeCollectionRef.current = collection.id;
      activeChannelRef.current = null;
      
      // Clear chips
      setChips([]);
      
      // Special case: "All" collection loads ALL items via fallbackSearch
      const isAllCollection = collection.name === 'All';
      
      console.log(`🔍 [handleCollectionClick] Loading collection "${collection.name}" (isAll: ${isAllCollection})`);
      
      if (isAllCollection) {
        // Load all items via existing fallbackSearch
        console.log('[handleCollectionClick] Loading "All" via fallbackSearch');
        const result = await fallbackSearch([], 0);
        setItems(dedupe(result.results || []));
        setNextCursor(result.nextCursor || null);
      } else {
        // Load collection content via join table ONLY
        console.log(`🔍 [handleCollectionClick] Loading via join table for collection: ${collection.id}`);
        const { data: collectionItems, error: collectionError } = await contentService.getContentByCollection(user.id, collection.id);
        
        if (collectionError) {
          console.error(`❌ Failed to load collection "${collection.name}":`, collectionError);
          setItems([]);
          setError(`Failed to load collection: ${collectionError.message}`);
        } else {
          console.log(`✅ Loaded ${collectionItems.length} items for collection "${collection.name}"`);
          setItems(dedupe(collectionItems || []));
          setNextCursor(null); // Collections don't paginate
        }
      }
      
      setError(null);
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading collection items:', error);
      setError('Failed to load collection items');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle filter hover from bottom bar
   * Shows/hides filters with 3 second delay
   */
  const handleFiltersHover = useCallback((isHovering) => {
    if (isHovering) {
      // Clear any existing timeout
      if (filterHoverTimeoutRef.current) {
        clearTimeout(filterHoverTimeoutRef.current);
        filterHoverTimeoutRef.current = null;
      }
      setShowFilters(true);
    } else {
      // Start 1.5 second countdown when mouse leaves bottom bar
      filterHoverTimeoutRef.current = setTimeout(() => {
        setShowFilters(false);
      }, 1500);
    }
  }, []);

  /**
   * Handle mouse enter on filter container
   * Keeps filters visible while hovering - cancels any timeouts
   */
  const handleFilterMouseEnter = useCallback(() => {
    if (filterHoverTimeoutRef.current) {
      clearTimeout(filterHoverTimeoutRef.current);
      filterHoverTimeoutRef.current = null;
    }
    setShowFilters(true);
  }, []);

  /**
   * Handle mouse leave on filter container
   * Hide filters after 3 seconds ONLY when leaving filters
   */
  const handleFilterMouseLeave = useCallback(() => {
    // Only hide if not hovering over bottom bar
    filterHoverTimeoutRef.current = setTimeout(() => {
      setShowFilters(false);
    }, 1500);
  }, []);

  // Handle delete content - show modal instead of browser confirm
  const handleDeleteContent = useCallback((item) => {
    if (!user?.id || !item?.id) return;
    setContentToDelete(item);
    setShowDeleteModal(true);
  }, [user?.id]);

  // Confirm deletion after modal confirmation
  const confirmDeleteContent = useCallback(async () => {
    if (!user?.id || !contentToDelete?.id) return;
    
    const itemIdToDelete = contentToDelete.id;
    const itemToRestore = { ...contentToDelete }; // Deep copy for rollback
    
    console.log('🗑️ Starting deletion for item:', itemIdToDelete);
    setDeletingContent(itemIdToDelete);
    setShowDeleteModal(false);
    
    // Mark as deleted immediately to prevent re-adding
    deletedItemsRef.current.add(itemIdToDelete);
    
    // Optimistic update: Remove from UI immediately
    setItems(prev => dedupe(prev.filter(i => i.id !== itemIdToDelete)));
    setContentToDelete(null);
    
    // Delete from server in background (non-blocking)
    contentService.deleteContent(user.id, itemIdToDelete)
      .then(({ error }) => {
        if (error) {
          console.error('❌ Failed to delete content:', error);
          // Remove from deleted set on error
          deletedItemsRef.current.delete(itemIdToDelete);
          // Re-add item if deletion failed (rollback)
          setItems(prev => {
            const exists = prev.some(i => i.id === itemIdToDelete);
            if (!exists) {
              console.log('🔄 Rolling back - re-adding item');
              return dedupe([...prev, itemToRestore]);
            }
            return dedupe(prev);
          });
          alert(`Failed to delete content: ${error.message || 'Unknown error'}`);
        } else {
          console.log('✅ Content deleted successfully from server');
        }
      })
      .catch((error) => {
        console.error('❌ Delete error:', error);
        // Remove from deleted set on error
        deletedItemsRef.current.delete(itemIdToDelete);
        // Rollback on error
        setItems(prev => {
          const exists = prev.some(i => i.id === itemIdToDelete);
          if (!exists) {
            console.log('🔄 Rolling back - re-adding item');
            return dedupe([...prev, itemToRestore]);
          }
          return dedupe(prev);
        });
        alert(`Failed to delete content: ${error.message || 'Unknown error'}`);
      })
      .finally(() => {
        setDeletingContent(null);
      });
  }, [user?.id, contentToDelete]);

  // Handle Smart Swirl click from OrganizationLayer
  const handleOrganizationSwirlClick = async (swirl) => {
    // MoodSync removed

    if (swirl.item_ids && swirl.item_ids.length > 0) {
      try {
        // Fetch items from deepsearch.items by ID only (no platform filtering)
        const { data: swirlItems, error: deepsearchError } = await supabase
          .schema('deepsearch').from('items')
          .select('auto_tags, meta')
          .in('id', swirl.item_ids)
          .limit(50);

        if (swirlItems && swirlItems.length > 0) {
          // Extract common themes from swirl items
          const allTags = {};
          swirlItems.forEach(item => {
            // Extract topics from multiple possible locations
            const topics = item.auto_tags?.topics || 
                          item.meta?.analysis_data?.tags || 
                          item.meta?.tags || 
                          [];
            topics.forEach((tag) => {
              allTags[tag] = (allTags[tag] || 0) + 1;
            });
          });

          // Get top 3 most common tags
          const topTags = Object.entries(allTags)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([tag]) => tag);

          // Convert to chips and filter
          const newChips = topTags.map(tag => ({
            id: crypto.randomUUID(),
            label: tag,
            kind: 'term'
          }));

          setChips(newChips);
          setActiveSwirlColor(swirl.color_palette);
          setActiveFilter(null);
          
          if (import.meta.env.DEV) {
            console.log('✅ Filtered by Smart Swirl:', swirl.title);
          }
        }
      } catch (error) {
        console.error('Error filtering by Smart Swirl:', error);
      }
    }
  };

  // Handle URL params: ?swirl= and ?theme=
  useEffect(() => {
    if (!user?.id) {
      if (import.meta.env.DEV) {
        console.log('🔍 [URL Params] Skipped - no user ID');
      }
      return;
    }
    
    const swirlParam = searchParams.get('swirl');
    const themeParam = searchParams.get('theme');
    
    if (import.meta.env.DEV) {
      const allParams = Object.fromEntries(searchParams.entries());
      console.log('🔍 [URL Params] Checking params:', {
        swirlParam: swirlParam || '(none)',
        themeParam: themeParam || '(none)',
        allParams,
        locationPathname: location.pathname,
        locationSearch: location.search,
        currentURL: window.location.href,
        searchString: window.location.search
      });
    }
    
    // Handle ?swirl= param - auto-open the swirl
    if (swirlParam) {
      const openSwirlById = async () => {
        try {
          const { data: swirl, error } = await smartSwirlsService.getSmartSwirlById(swirlParam);
          if (!error && swirl && swirl.user_id === user.id) {
            // Use the same logic as handleOrganizationSwirlClick
            await handleOrganizationSwirlClick(swirl);
            // Switch to swirls tab if available
            setActiveOrgTab('swirls');
          }
        } catch (error) {
          console.error('Error loading swirl from URL param:', error);
        }
      };
      openSwirlById();
    }
    
    // Handle ?theme= param - filter items by theme
    if (themeParam) {
      if (import.meta.env.DEV) {
        console.log('🎯 [Theme Filter] Theme param detected:', themeParam);
      }
      const filterByTheme = async () => {
        try {
          // Decode the theme
          const decodedTheme = decodeURIComponent(themeParam);
          const themeLower = decodedTheme.toLowerCase();
          
          if (import.meta.env.DEV) {
            console.log('🎯 [Theme Filter] Decoded theme:', decodedTheme, '→ normalized:', themeLower);
          }
          
          // Set loading state
          setLoading(true);
          setChips([]);
          setActiveFilter('theme');
          setThemeFilter(decodedTheme);
          
          if (import.meta.env.DEV) {
            console.log('✅ Filtering by theme from URL:', decodedTheme);
          }
          
          // Fetch all user items to filter locally by topic
          const { data: session } = await supabase.auth.getSession();
          if (!session?.session?.user?.id) {
            setLoading(false);
            return;
          }
          
          // Query deepsearch.items for items with matching topics
          const { data: allItems, error } = await supabase
            .schema('deepsearch')
            .from('items')
            .select('*')
            .eq('user_id', session.session.user.id)
            .order('created_at', { ascending: false })
            .limit(200);
          
          if (error) {
            console.error('Error fetching items for theme filter:', error);
            setItems([]);
            setLoading(false);
            return;
          }
          
          
          // Filter items using keyword-based theme classification (fast, no API calls)
          // Reset debug counter for fresh logging
          resetDebugCount();
          
          if (import.meta.env.DEV) {
            console.log('\n' + '='.repeat(70));
            console.log(`🎯 THEME FILTER: "${decodedTheme}" - Processing ${allItems?.length || 0} items`);
            console.log('='.repeat(70));
          }
          
          const filtered = (allItems || []).filter(item => 
            itemMatchesTheme(item, decodedTheme, { debug: false })
          );
          
          if (import.meta.env.DEV) {
            console.log('\n' + '-'.repeat(70));
            console.log(`📊 RESULTS: ${filtered.length}/${allItems?.length || 0} items matched "${decodedTheme}"`);
            console.log('-'.repeat(70));
            
            // Log first 3 matched items
            if (filtered.length > 0) {
              console.log('\n✅ Sample matched items:');
              filtered.slice(0, 3).forEach((item, i) => {
                console.log(`   ${i + 1}. "${item.title?.slice(0, 50)}"`);
              });
            }
          }
          
          setItems(filtered);
          setLoading(false);
        } catch (error) {
          console.error('Error filtering by theme from URL param:', error);
          setItems([]);
          setLoading(false);
        }
      };
      filterByTheme();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, searchParams, location.search]);

  // Feature flag guard
  if (!FLAGS.SWIRLSPACE_ENABLED) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="mx-auto max-w-4xl px-6 py-24 text-center">
          <h1 className="text-3xl font-semibold text-neutral-800 mb-3">Swirlspace</h1>
          <p className="mt-3 text-neutral-600">
            This feature is disabled. Set <code className="px-2 py-1 bg-neutral-100 rounded">VITE_SWIRLSPACE_ENABLED=true</code> to preview.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-cyan-50 relative overflow-hidden">
      {/* Existing Sidebar - same as all other pages */}
      <Sidebar
        isExpanded={isExpanded}
        onToggle={toggle}
      />

      {/* Arrow for minimized sidebar */}
      {!isExpanded && (
        <button
          onClick={toggle}
          className="fixed left-16 top-8 w-6 h-6 bg-white/40 backdrop-blur-lg rounded-r-lg flex items-center justify-center text-gray-800 hover:bg-white/50 transition-all duration-500 ease-out z-[60] shadow-lg border border-white/30"
        >
          <ChevronRight size={16} />
        </button>
      )}

      {/* Main Content Area - same layout as SwirlDashboard */}
      <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out relative ${isExpanded ? 'ml-72' : 'ml-16'}`}>
        <div className="swirlspace-root relative min-h-full">
          {/* MoodSync removed - was causing green background issues */}

          {/* Floating Search Bar - With blur backdrop like MyMind */}
          {isScrolled && (
          <div 
            className="fixed z-50 pointer-events-none opacity-100"
            style={{
              top: 0,
              left: isExpanded ? '288px' : '64px',
              right: 0,
              transition: 'opacity 0.3s ease-out, left 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {/* Blur gradient backdrop - smooth fade */}
            <div 
              className="absolute inset-x-0 top-0 h-16"
              style={{
                background: 'linear-gradient(to bottom, rgba(249, 249, 248, 0.85) 0%, rgba(249, 249, 248, 0.4) 40%, rgba(249, 249, 248, 0.1) 70%, rgba(249, 249, 248, 0) 100%)',
              }}
            />
            
            {/* Search bar container */}
            <div 
              className={`relative flex justify-center pt-4 ${isScrolled ? 'pointer-events-auto' : ''}`}
            >
              <div 
                className="flex items-center gap-4 bg-white rounded-full border border-gray-200/60 px-7 py-3.5 shadow-lg"
                style={{
                  width: '70%',
                  maxWidth: '800px',
                  minWidth: '480px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
                }}
              >
              {/* Sparkle Icon */}
              <svg 
                width="22" 
                height="22" 
                viewBox="0 0 24 24" 
                fill="none"
                className="text-purple-500 flex-shrink-0"
              >
                <path 
                  d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z" 
                  fill="currentColor"
                />
              </svg>
              <div className="flex-1">
                <ChipInput
                  value={chips}
                  onChange={setChips}
                  placeholder="Search your Swirlspace..."
                  autoFocus={false}
                  hideSuggestions={true}
                  compact={true}
                  onSubmit={() => {
                    const scrollContainer = document.querySelector('main.overflow-y-auto');
                    if (scrollContainer) {
                      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                />
              </div>
            </div>
          </div>
          </div>
          )}

          {/* Main Search Area - Scrolls away normally */}
          <div className="px-8 pt-8 pb-4">
            {/* Title */}
            <h1 className="text-5xl font-serif mb-4">
              Search your <span className="text-[#7A47F5]">Swirlspace</span>...
            </h1>
            
            {/* Search Bar */}
            <div className="mb-3">
              <ChipInput
                value={chips}
                onChange={setChips}
                placeholder="Search your Swirlspace..."
                autoFocus={false}
              />
            </div>

            {/* Filter Pills Row */}
            <div className="mb-4">
              <FilterLayer
                onFilterClick={handleFilterLayerClick}
                activeFilter={activeFilter}
                scrollToOrganizationLayer={scrollToOrganizationLayer}
              />
            </div>
          </div>

          <div className="relative z-10 px-8 -mt-4">
              {/* Organization Tabs - Compact Pill Style */}
              <div className="flex items-center gap-2 mb-6">
                {[
                  { id: 'all', label: 'All', Icon: Grid3x3 },
                  { id: 'recent', label: 'Recently Viewed', Icon: Clock },
                  { id: 'collections', label: 'Collections', Icon: Folder },
                ].map((tab) => {
                  const isActive = activeOrgTab === tab.id;
                  const TabIcon = tab.Icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveOrgTab(tab.id);
                        if (tab.id === 'all') {
                          setSelectedCollectionId(null);
                          window.dispatchEvent(new CustomEvent('clearActiveFilters'));
                        }
                        // Note: 'recent' tab content is handled by RecentlyViewedItems component
                      }}
                      className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isActive
                          ? 'bg-white text-neutral-900 shadow-sm'
                          : 'bg-transparent text-neutral-500 hover:bg-white/40 hover:text-neutral-700'
                      }`}
                    >
                      <TabIcon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">
              {error}
            </div>
          )}

          {/* Search Mode Indicator */}
          {searchMode && items.length > 0 && (() => {
            const modeConfig = {
              'semantic': {
                label: 'Semantic AI',
                color: 'bg-purple-500',
                tooltip: 'Using AI to find items with similar meaning to your search',
              },
              'semantic-soft': {
                label: 'Hybrid search',
                color: 'bg-gradient-to-r from-purple-500 to-blue-500',
                tooltip: 'Combined semantic and keyword matching for broader results',
              },
              'semantic-nearest': {
                label: 'Similar items',
                color: 'bg-orange-400',
                tooltip: 'Showing closest matches by content similarity',
              },
              'keyword': {
                label: 'Keyword search',
                color: 'bg-blue-500',
                tooltip: 'Matching exact words and phrases in your content',
              },
              'hybrid': {
                label: 'Hybrid search',
                color: 'bg-gradient-to-r from-purple-500 to-blue-500',
                tooltip: 'Combined semantic and keyword matching',
              },
              'color-tag': {
                label: 'Color match',
                color: 'bg-gradient-to-r from-red-400 via-yellow-400 to-blue-400',
                tooltip: 'Matching items by detected colors',
              },
              'similar': {
                label: 'Similar items',
                color: 'bg-orange-400',
                tooltip: 'Finding visually or contextually similar items',
              },
              'none': {
                label: 'No strong matches',
                color: 'bg-gray-400',
                tooltip: 'Results below similarity threshold',
              },
              'empty': {
                label: 'No results',
                color: 'bg-gray-300',
                tooltip: 'No matching items found',
              },
            };
            const config = modeConfig[searchMode] || modeConfig['semantic'];
            
            return (
              <div className="mb-4 pl-4 relative z-20">
                <div 
                  className="inline-flex items-center gap-1.5 group cursor-help"
                  title={config.tooltip}
                >
                  <span className={`w-2 h-2 rounded-full ${config.color} ${searchMode === 'semantic' ? 'animate-pulse' : ''}`}></span>
                  <span className="text-xs text-gray-500 font-medium">{config.label}</span>
                  {/* Tooltip on hover */}
                  <span className="absolute left-4 top-6 z-50 hidden group-hover:block px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg whitespace-nowrap">
                    {config.tooltip}
                  </span>
                </div>
              </div>
            );
          })()}

          {/* Color Fallback Message */}
          {searchMeta?.colorQuery && searchMeta.colorQuery.length > 0 && items.length > 0 && (
            <div className="mb-3 pl-4 relative z-20">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-red-50 via-yellow-50 to-blue-50 rounded-full text-xs text-gray-600">
                <span className="w-3 h-3 rounded-full bg-gradient-to-r from-red-400 via-yellow-400 to-blue-400"></span>
                Matched colors: {searchMeta.colorQuery.join(', ')}
              </span>
            </div>
          )}

          {/* Theme Filter Indicator */}
          {activeFilter === 'theme' && themeFilter && items.length > 0 && (
            <div className="mb-3 pl-4 relative z-20">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 rounded-full text-sm text-purple-700">
                <span className="font-medium">Showing: {themeFilter}</span>
                <button
                  onClick={() => {
                    // Full page reload to clean Swirlspace - most reliable way to reset all state
                    window.location.href = '/swirlspace';
                  }}
                  className="ml-1 w-5 h-5 rounded-full bg-purple-200 hover:bg-purple-300 flex items-center justify-center text-purple-600 transition-colors cursor-pointer"
                >
                  ×
                </button>
              </span>
            </div>
          )}

          {/* Theme Filter Empty State */}
          {activeFilter === 'theme' && !loading && items.length === 0 && (
            <div className="w-full py-16 flex flex-col items-center justify-center text-center text-neutral-500 relative z-50">
              <span className="text-lg font-medium">No discoveries found for "{themeFilter}" yet.</span>
              <span className="text-sm mt-1">Explore more content and Swirl will start connecting patterns for you.</span>
              <a
                href="/swirlspace"
                className="mt-4 px-4 py-2 rounded-full bg-purple-100 text-purple-700 text-sm font-medium hover:bg-purple-200 transition-colors cursor-pointer inline-block"
              >
                View all discoveries
              </a>
            </div>
          )}

          {/* Organization Layer - Tabs with content (centered) */}
          <div className="mt-2 relative z-10 px-8">
            <OrganizationLayer
              activeTab={activeOrgTab}
              onSwirlClick={handleOrganizationSwirlClick}
              onCollectionClick={handleCollectionClick}
              items={items}
              totalItemCount={totalItemCount}
              chips={chips}
              loading={loading}
              onFilterClick={handleFilterLayerClick}
              onItemDelete={handleDeleteContent}
              searchMeta={searchMeta}
              activeFilter={activeFilter}
              onItemClick={(item) => {
                // SPEED FIX: Open modal IMMEDIATELY with existing data
                // No database fetch needed - we already have the item data!
                
                // Track view and update context in background (non-blocking)
                if (user?.id && item.id) {
                  contentService.trackItemView(item.id, user.id).catch(() => {});
                  personalizationService.updateContextFromItem(item).catch(() => {});
                }
                
                // Map the item to modal format and show immediately
                const mappedItem = {
                  ...item,
                  id: item.id,
                  title: item.title || item.description || 'Untitled',
                  description: item.description,
                  url: item.url,
                  source_type: item.source_type || item.content_type,
                  contentType: item.source_type || item.content_type,
                  channel: item.channel || item.meta?.channel || (item.source_type ? item.source_type.charAt(0).toUpperCase() + item.source_type.slice(1) : 'Content'),
                  thumbnail_url: item.thumbnail_url || item.url || item.image_url,
                  analysis_data: item.analysis_data || item.meta?.analysis_data || {
                    summary: item.meta?.summary || item.description,
                    ocr: { text: item.extracted_text },
                    colors: { primaryColor: item.primary_color, mood: item.color_mood, palette: item.color_palette },
                    tagging: { primaryTags: item.primary_tags, allTags: item.auto_tags },
                  },
                  extracted_text: item.extracted_text,
                  auto_tags: item.auto_tags,
                  primary_tags: item.primary_tags,
                  primary_color: item.primary_color,
                  color_mood: item.color_mood,
                  color_palette: item.color_palette,
                  meta: item.meta,
                  created_at: item.created_at,
                  _source: item._source || (item.source_type === 'image' ? 'deepsearch.items' : 'instagram_content'),
                };
                
                setSelectedContent(mappedItem);
                setShowContentModal(true);
              }}
            />
          </div>
          </div>

      </main>

      {/* Floating Sidebar for Smart Swirls - Classic sidebar under flag */}
      {FLAGS.CLASSIC_SIDEBAR_ENABLED && (
        <FloatingSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          activeColorPalette={activeSwirlColor}
          onSwirlClick={async (swirl) => {
            await handleOrganizationSwirlClick(swirl);
          }}
        />
      )}

      {/* Content Detail Modal */}
      <ContentDetailModal
        isOpen={showContentModal}
        onClose={() => {
          setShowContentModal(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setContentToDelete(null);
        }}
        onConfirm={confirmDeleteContent}
        title="Delete Content"
        itemName={contentToDelete?.title}
        itemType="content"
        warningMessage="This will permanently remove the content and all its analysis data."
        isLoading={deletingContent === contentToDelete?.id}
      />
    </div>
  );
}
